/**
 * SessionHistoryDrawer — collapsible left-rail inside the chat panel
 * listing the user's recent Copilot conversations. Click a row to resume
 * it; hover for the delete button.
 *
 * Deliberately simple: no search, no infinite scroll. Vooxy Copilot is
 * meant to be a quick-ask tool, not a long-term knowledge base — so the
 * most recent 25 sessions is plenty for an MVP.
 */

import React from 'react';
import { Trash, Plus, ChatText } from '@phosphor-icons/react';

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffH = (now - d) / 36e5;
  if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m ago`;
  if (diffH < 24) return `${Math.floor(diffH)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function SessionHistoryDrawer({ sessions, activeId, onOpen, onDelete, onNew }) {
  return (
    <div className="flex flex-col h-full border-r border-slate-200 bg-slate-50" data-testid="copilot-session-drawer">
      <div className="p-2 border-b border-slate-200 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-2">History</span>
        <button
          onClick={onNew}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
          data-testid="copilot-new-conversation"
          title="Start a new conversation"
        >
          <Plus size={11} weight="bold" /> New
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {sessions.length === 0 ? (
          <div className="p-3 text-xs text-slate-400 italic">No conversations yet.</div>
        ) : (
          <ul className="py-1">
            {sessions.map((s) => (
              <li key={s.id} className="group px-1">
                <button
                  onClick={() => onOpen(s.id)}
                  className={`w-full text-left px-2 py-1.5 rounded flex items-start gap-1.5 ${
                    s.id === activeId ? 'bg-emerald-50 text-emerald-900' : 'hover:bg-white text-slate-700'
                  }`}
                  data-testid={`copilot-session-row-${s.id}`}
                >
                  <ChatText size={12} className="mt-0.5 shrink-0 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{s.title || 'Untitled'}</div>
                    <div className="text-[10px] text-slate-400">{fmtTime(s.updated_at)} · {s.message_count || 0} msgs</div>
                  </div>
                </button>
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                    className="absolute -top-6 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                    title="Delete conversation"
                    data-testid={`copilot-session-delete-${s.id}`}
                  >
                    <Trash size={11} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
